import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { BaseSignalRService } from './base-signalR.service';
import { SignalRConnectionManagerService } from './signalr-connection-manager.service';
import { NotificationStateService } from './notification-state.service';
import { JobPriorityUpdateNotification } from '../models/job-priority-notification.models';
import { DispatchAlertNotification } from '../models/dispatch-alert-notification.models';

@Injectable({
    providedIn: 'root'
})
export class NotificationService {
    private readonly signalRService = inject(BaseSignalRService);
    private readonly connectionManager = inject(SignalRConnectionManagerService);
    private readonly notificationStateService = inject(NotificationStateService);

    // State management
    private _connectedUsers = new BehaviorSubject<string[]>([]);

    // Expose observables for component consumption
    readonly connectedUsers$ = this._connectedUsers.asObservable();
    readonly connectionEstablished$ = this.signalRService.connectionEstablished;
    readonly reconnecting$ = this.signalRService.reconnecting;
    readonly connectionStatus$ = this.connectionManager.connectionStatus;
    readonly queueLength$ = this.connectionManager.queueLength;
    readonly errorMessage$ = this.connectionManager.errorMessage;

    /**
     * Initialize the chat service
     * @param userId The ID of the current user
     */
    async initialize(userId: string): Promise<void> {
        try {
            // Use connection manager instead of direct connection
            await this.connectionManager.requestConnection(userId, 'high');
            console.log('Notification Service initialized');

            // Set up specific message handlers
            this.setupMessageHandlers();
        } catch (error) {
            console.error('Failed to initialize Notification service:', error);
            throw error;
        }
    }

    /**
     * Set up message handlers specific to the chat service
     */
    private setupMessageHandlers(): void {
        // Listen for broadcast messages
        this.connectionManager.listenFor<any>('broadcastMessage').subscribe(message => {
            console.log('Broadcast message received:', message);
            // Handle broadcast message (e.g., update UI, show notification)
        });

        this.connectionManager.listenFor<any>('completeMessage').subscribe(message => {
            console.log('completeMessage message received:', message);
            // Handle broadcast message (e.g., update UI, show notification)
        });

        this.connectionManager.listenFor<any>('createJobMessage').subscribe(message => {
            console.log('createJobMessage message received:', message);
            // Handle broadcast message (e.g., update UI, show notification)
        });

        this.connectionManager.listenFor<any>('JobChangeStatus').subscribe(message => {
            // Handle broadcast message (e.g., update UI, show notification)
        });

        this.connectionManager.listenFor<any>('JobTransfer').subscribe(message => {
            // Job list components handle this notification directly.
        });

        this.connectionManager.listenFor<any>('FinishJob').subscribe(message => {
            // Handle broadcast message (e.g., update UI, show notification)
        });

        this.connectionManager.listenFor<any>('JobPriorityUpdated').subscribe(message => {
            try {
                let notification: JobPriorityUpdateNotification;

                // Parse message if it's a string
                if (typeof message === 'string') {
                    notification = JSON.parse(message);
                } else {
                    notification = message;
                }

                // Add to notification state
                this.notificationStateService.addJobPriorityNotification(notification);
                console.log('Job priority notification added to state:', notification);
            } catch (error) {
                console.error('Error processing JobPriorityUpdated message:', error, message);
            }
        });

        this.connectionManager.listenFor<any>('DispatchAlert').subscribe(message => {
            try {
                let notification: DispatchAlertNotification;

                // Parse message if it's a string
                if (typeof message === 'string') {
                    notification = JSON.parse(message);
                } else {
                    notification = message;
                }

                // Add to notification state
                this.notificationStateService.addDispatchAlertNotification(notification);
                console.log('Dispatch alert notification added to state:', notification);
            } catch (error) {
                console.error('Error processing DispatchAlert message:', error, message);
            }
        });

        // Listen for private messages
        this.connectionManager.listenFor<any>('privateMessage').subscribe(message => {
            console.log('Private message received:', message);
            // Handle private message
        });

        // Listen for user connection events
        this.connectionManager.listenFor<{ userId: string }>('userConnected').subscribe(data => {
            console.log(`User connected: ${data.userId}`);
            const currentUsers = this._connectedUsers.value;
            if (!currentUsers.includes(data.userId)) {
                this._connectedUsers.next([...currentUsers, data.userId]);
            }
        });

        // Listen for user disconnection events
        this.connectionManager.listenFor<{ userId: string }>('userDisconnected').subscribe(data => {
            console.log(`User disconnected: ${data.userId}`);
            const currentUsers = this._connectedUsers.value;
            this._connectedUsers.next(currentUsers.filter(id => id !== data.userId));
        });

        // Listen for connected users list
        this.connectionManager.listenFor<{ users: string[] }>('connectedUsers').subscribe(data => {
            console.log('Connected users:', data.users);
            this._connectedUsers.next(data.users);
        });
    }

    /**
     * Get the current user ID
     */
    getCurrentUserId(): string {
        // This should be implemented according to your authentication system
        return this.signalRService['hubConnection']?.connectionId || '';
    }

    /**
     * Send a message to all users
     * @param message The message to send
     */
    async sendMessage(message: string): Promise<void> {
        await this.connectionManager.sendMessage(message);
    }

    /**
     * Send a private message to a specific user
     * @param userId The ID of the user to send the message to
     * @param message The message to send
     */
    async sendPrivateMessage(userId: string, message: string): Promise<void> {
        await this.connectionManager.sendPrivateMessage(userId, message);
    }

    /**
     * Check if the SignalR connection is established
     */
    isConnected(): boolean {
        return this.connectionManager.isConnectionHealthy();
    }

    /**
     * Disconnect from the chat service
     */
    async disconnect(): Promise<void> {
        await this.connectionManager.disconnect();
    }

    /**
     * Get connection queue status
     */
    getQueueStatus() {
        return this.connectionManager.getQueueStatus();
    }

    /**
     * Cancel connection request for a specific user
     */
    cancelConnection(userId: string): boolean {
        return this.connectionManager.cancelConnection(userId);
    }

    /**
     * Listen for Excel import completion notifications
     */
    listenForExcelImportNotifications() {
        return this.connectionManager.listenFor<any>('ExcelImportCompleted');
    }
}
